import { useCallback } from 'react';
import { formErrorAppearedDissapeared } from '@wix/bi-logger-forms-platform-uou/v2';
import { EXPERIMENTS, useAddressForms, useExperiments, useExternalData, } from '@wix/form-fields';
import { findErrorByNormalizedPath, getFieldErrors, mergeErrors, validate, } from '../services';
import { runAdditionalValidation } from '../services/additional-field-validation';
export const reportErrorChanges = ({ bi, existingErrors, newErrors, form, }) => {
    const newlyAddedErrors = newErrors.filter((newError) => !existingErrors.find((error) => newError.errorPath === error.errorPath &&
        newError.errorType === error.errorType));
    const removedErrors = existingErrors.filter((error) => !newErrors.find((newError) => error.errorPath === newError.errorPath &&
        error.errorType === newError.errorType));
    newlyAddedErrors.forEach((error) => {
        const field = form.fields?.find((_field) => _field.target === error.errorPath);
        bi.report(formErrorAppearedDissapeared({
            action_type: 'appeared',
            error_name: error.errorMessage,
            field_id: field?.id,
            field_target: field?.target && 'target' in field ? field.target : undefined,
            field_name: field?.view?.label,
        }));
    });
    removedErrors.forEach((error) => {
        const field = form.fields?.find((_field) => _field.target === error.errorPath);
        bi.report(formErrorAppearedDissapeared({
            action_type: 'disappeared',
            error_name: error.errorMessage,
            field_id: field?.id,
            field_target: field?.target && 'target' in field ? field.target : undefined,
            field_name: field?.view?.label,
        }));
    });
    return { newlyAddedErrors, removedErrors };
};
export const useValidation = ({ form, errorsRef, setErrors, validationMode, }) => {
    const { addressTemplates } = useAddressForms();
    const externalData = useExternalData();
    const experiments = useExperiments();
    const isRemoveFalsyValuesEnabled = experiments.isExperimentEnabled(EXPERIMENTS.REMOVE_FALSY_VALUES);
    const validateFields = useCallback(async (newValues) => {
        // TODO: returns undefined when form-id missing
        const result = await validate(form, newValues, addressTemplates, validationMode);
        const _errors = result?.errors || [];
        const additionalErrors = runAdditionalValidation({
            form,
            values: newValues,
            externalData,
        });
        const allErrors = mergeErrors({ errors: _errors, additionalErrors });
        setErrors(allErrors);
        return allErrors;
    }, [form, setErrors, addressTemplates, externalData, validationMode]);
    const handleFieldValidation = useCallback(async (fieldsToValidate) => {
        const fieldErrors = await getFieldErrors(fieldsToValidate, form, isRemoveFalsyValuesEnabled, addressTemplates, externalData, validationMode);
        const otherErrors = (errorsRef.current ?? []).filter((error) => {
            return !findErrorByNormalizedPath(fieldsToValidate, error.errorPath);
        });
        const allErrors = [...otherErrors, ...fieldErrors];
        setErrors(allErrors);
        return allErrors;
    }, [
        errorsRef,
        form,
        setErrors,
        addressTemplates,
        externalData,
        validationMode,
        isRemoveFalsyValuesEnabled,
    ]);
    const validateStep = useCallback(async (stepId, newValues) => {
        const items = form?.steps?.find((step) => step.id === stepId)?.layout?.large?.items ||
            [];
        const validationErrors = await Promise.all(items.map(async (item) => {
            const field = form?.fields?.find((_field) => _field.id === item.fieldId);
            const fieldValidationResult = field?.target
                ? await handleFieldValidation([
                    {
                        path: field.target,
                        value: newValues[field.target],
                    },
                ])
                : [];
            return fieldValidationResult;
        }));
        const validationErrorsFlatten = validationErrors.flat();
        setErrors(validationErrorsFlatten);
        return !validationErrorsFlatten.length;
    }, [form, setErrors, handleFieldValidation]);
    return { validateFields, validateStep, handleFieldValidation };
};
//# sourceMappingURL=use-validation.js.map